import { PpLocaleProvider } from '@pperform/pp-react-components/PpLocaleProvider';
import { PpLanguageProvider } from '@pperform/pp-react-components/PpLanguages';
import { StrictMode } from 'preact/compat';
import { useCallback, useEffect } from 'preact/hooks';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from 'react-query';

import ErrorBoundary from 'components/ErrorBoundary';
import FontLoader from 'components/FontLoader';
import ScopedCssBaseLine from 'components/ScopedCssBaseLine';
import WidgetWrapper from 'components/WidgetWrapper';
import CONFIGURABLE_CONSTANTS from 'constants/configurable.constants';

import { FocusProvider } from 'contexts/FocusContext';
import { ptpLanguageI18n } from 'i18n';
import GlobalConfigProvider from 'GlobalConfig.context';
import { FeatureConfigProvider } from 'FeatureConfig.context';

import { useAppRoot } from 'hooks/useAppRoot/useAppRoot';
import { ptpClient } from 'services/sentry.services';
import App from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

const AppRootInner = ({ cmsSlug }: { cmsSlug: string }) => {
  const {
    data: { locale, globalConfig, featureToggles },
    isSuccess,
    error,
  } = useAppRoot({ cmsSlug });

  const onSubscribeLanguageChange = useCallback(
    (callback: (language: string) => void) => {
      callback(globalConfig?.config?.language);
    },
    [globalConfig?.config?.language],
  );

  useEffect(() => {
    if (!CONFIGURABLE_CONSTANTS.sentryDns) return;
    ptpClient.init();
  }, []);

  // if queries are pending/processing, we will show empty screen until we has UI for those status
  if (!isSuccess) {
    return null;
  }

  if (error) {
    return <h1>[PTP] Something went wrong.</h1>;
  }

  return (
    <PpLanguageProvider
      i18n={ptpLanguageI18n}
      subscribeLanguageChange={onSubscribeLanguageChange}
    >
      <PpLocaleProvider locale={locale}>
        <GlobalConfigProvider contextValue={{ globalConfig }}>
          <FeatureConfigProvider contextValue={{ featureToggles }}>
            <ThemeProvider theme={{ configs: globalConfig.config }}>
              <FontLoader>
                <ScopedCssBaseLine>
                  <WidgetWrapper>
                    <FocusProvider>
                      <App />
                    </FocusProvider>
                  </WidgetWrapper>
                </ScopedCssBaseLine>
              </FontLoader>
            </ThemeProvider>
          </FeatureConfigProvider>
        </GlobalConfigProvider>
      </PpLocaleProvider>
    </PpLanguageProvider>
  );
};

const AppRoot = ({ cmsSlug }: { cmsSlug: string }) => {
  return (
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AppRootInner cmsSlug={cmsSlug} />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
};

export { AppRoot };
